import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabaseClient";
import packageJson from "../../package.json";
import { useLocation, useNavigate } from "react-router-dom";

import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { LoadingButtonContent } from "@/components/ui/loading-button-content";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { timezones } from "@/lib/timezones";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    Check,
    ChevronsUpDown,
    Globe,
    User,
    Settings2,
    Clock,
    RefreshCw,
    Trash2,
} from "lucide-react";
import { resetMockData } from "@/lib/mockData";
import { cn } from "@/lib/utils";
import { TimezoneSelector } from "@/components/settings/TimezoneSelector";

const optimizationFormSchema = z.object({
    minPrice: z.number(),
    maxPrice: z.number(),
    cost: z.number().min(0).max(1000000),
    optimizationInterval: z.number(),
});

const tabs = [
    {
        name: "General",
        value: "general",
        icon: User,
    },
    {
        name: "Optimizations",
        value: "optimizations",
        icon: Settings2,
    },
    {
        name: "Timezones",
        value: "timezones",
        icon: Clock,
    },
];

function Settings() {
    const { session, isDemoMode, shop } = useAuth();
    const [loading, setLoading] = useState(false);
    const [message] = useState<{
        type: "success" | "error";
        text: string;
    } | null>(null);
    const [isDemoDataResetting, setIsDemoDataResetting] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const [marketTimezones, setMarketTimezones] = useState(
        shop?.settings?.timezones || {}
    );

    const markets = useCallback(() => {
        return Object.keys(shop?.markets || {});
    }, [shop]);

    const { toast } = useToast();

    const optimizationForm = useForm<z.infer<typeof optimizationFormSchema>>({
        resolver: zodResolver(optimizationFormSchema),
        defaultValues: {
            minPrice: shop?.settings?.optimizations?.min_price || -25,
            maxPrice: shop?.settings?.optimizations?.max_price || 25,
            cost: shop?.settings?.optimizations?.cost || 20,
            optimizationInterval:
                shop?.settings?.optimizations?.optimization_interval || 86400, // 24 hours in seconds
        },
    });

    useEffect(() => {
        setMarketTimezones(shop?.settings?.timezones || {});
        // Update form values when shop settings change
        if (shop?.settings?.optimizations) {
            optimizationForm.reset({
                minPrice: shop.settings.optimizations.min_price,
                maxPrice: shop.settings.optimizations.max_price,
                cost: shop.settings.optimizations.cost,
                optimizationInterval:
                    shop.settings.optimizations.optimization_interval,
            });
        }
    }, [shop, optimizationForm]);

    // Returns the timezone for a given market, using default for the market if not set
    const getMarketTimezone = (market: string) => {
        if (!marketTimezones?.default && !marketTimezones?.user_defined) {
            console.log("No timezone set for market", market);
            console.log(marketTimezones);
            return "Europe/London";
        }
        if (marketTimezones?.user_defined?.[market]) {
            return marketTimezones?.user_defined?.[market];
        }
        if (marketTimezones?.default?.[market]) {
            return marketTimezones?.default?.[market];
        }
        return "Europe/London";
    };

    // Get the initial tab from the URL hash, defaulting to 'general' if no hash
    const initialTab = location.hash.slice(1) || "general";

    // Update URL hash when tab changes
    const handleTabChange = (value: string) => {
        navigate(`#${value}`, { replace: true });
    };

    const handleMarketTimezoneChange = async (
        market: string,
        timezone: string
    ) => {
        const newMarketTimezones = {
            ...marketTimezones,
            user_defined: {
                ...marketTimezones.user_defined,
                [market]: timezone,
            },
        };
        setMarketTimezones(newMarketTimezones);

        // Push to supabase
        const { error } = await supabase
            .from("shops")
            .update({
                settings: {
                    ...shop?.settings,
                    timezones: newMarketTimezones,
                },
            })
            .eq("user_id", session?.user.id);

        console.log({
            shop: shop?.shop,
            settings: {
                ...shop?.settings,
                timezones: newMarketTimezones,
            },
        });
        if (error) throw error;
        toast({
            title: "Market timezone updated successfully",
        });
    };

    const handleOptimizationSubmit = async (
        values: z.infer<typeof optimizationFormSchema>
    ) => {
        setLoading(true);
        try {
            const { error } = await supabase
                .from("shops")
                .update({
                    settings: {
                        ...shop?.settings,
                        optimizations: {
                            min_price: values.minPrice,
                            max_price: values.maxPrice,
                            cost: values.cost,
                            timezone: values.timezone,
                            market: values.market,
                            optimization_interval: values.optimizationInterval,
                        },
                    },
                })
                .eq("user_id", session?.user.id);

            if (error) throw error;
            toast({
                title: "Optimization settings updated successfully",
            });
        } catch {
            toast({
                title: "Failed to update optimization settings",
                description:
                    "Try again. If this issue persists, contact us at support@getoptifi.com.",
                variant: "destructive",
            });
        }
        setLoading(false);
    };

    const handleDeleteAccount = () => {
        window.location.href = `mailto:support@getoptifi.com?subject=Account Deletion Request&body=Please delete my account (${session?.user?.email}).`;
    };

    const handleResetDemoData = async () => {
        if (!session?.user?.id) return;
        setIsDemoDataResetting(true);
        await resetMockData(session.user.id);
        setIsDemoDataResetting(false);
    };

    return (
        <div className="space-y-6">
            {message && (
                <Alert
                    variant={
                        message.type === "error" ? "destructive" : "default"
                    }
                >
                    <AlertTitle>
                        {message.type === "error" ? "Error" : "Success"}
                    </AlertTitle>
                    <AlertDescription>{message.text}</AlertDescription>
                </Alert>
            )}

            <Tabs
                defaultValue={initialTab}
                value={initialTab}
                onValueChange={handleTabChange}
                className="w-full"
            >
                <TabsList className="w-full p-0 bg-background justify-start border-b rounded-none">
                    {tabs.map((tab) => (
                        <TabsTrigger
                            key={tab.value}
                            value={tab.value}
                            className="rounded-none bg-background h-full data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-primary px-4 py-2"
                        >
                            <div className="flex items-center gap-2">
                                <tab.icon className="h-4 w-4" />
                                <span>{tab.name}</span>
                            </div>
                        </TabsTrigger>
                    ))}
                </TabsList>

                <div className="mt-6">
                    <TabsContent value="general" className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Account Details</CardTitle>
                                <CardDescription>
                                    Your account information
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <div className="text-sm font-medium">
                                        Email
                                    </div>
                                    <div className="text-sm text-muted-foreground">
                                        {session?.user?.email}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm font-medium">
                                        Last Sign In
                                    </div>
                                    <div className="text-sm text-muted-foreground">
                                        {session?.user?.last_sign_in_at
                                            ? new Date(
                                                  session.user.last_sign_in_at
                                              ).toLocaleString()
                                            : "N/A"}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm font-medium">
                                        Account ID
                                    </div>
                                    <div className="text-sm text-muted-foreground">
                                        {session?.user?.id}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm font-medium">
                                        Optifi Version
                                    </div>
                                    <div className="text-sm text-muted-foreground">
                                        {packageJson.version}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {isDemoMode && (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Demo Mode</CardTitle>
                                    <CardDescription>
                                        You are currently in demo mode. Some
                                        features may be limited. To reset the
                                        mock demo data, click the button below.
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <Button
                                        onClick={handleResetDemoData}
                                        disabled={isDemoDataResetting}
                                    >
                                        <RefreshCw
                                            className={`w-4 h-4 ${
                                                isDemoDataResetting
                                                    ? "animate-spin"
                                                    : ""
                                            }`}
                                        />
                                        Reset Demo
                                    </Button>
                                </CardContent>
                            </Card>
                        )}

                        <Card>
                            <CardHeader>
                                <CardTitle>Delete Account</CardTitle>
                                <CardDescription>
                                    Contact support to request permanent
                                    deletion of your account. You will be
                                    redirected to email our support team to
                                    process your account deletion request. We
                                    will process your request within 3 business
                                    days.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <Button variant="destructive">
                                            <Trash2 className="w-4 h-4" />
                                            Delete Account
                                        </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>
                                                Are you sure you want to delete
                                                your account?
                                            </AlertDialogTitle>
                                            <AlertDialogDescription>
                                                Click the button below to be
                                                redirected to email our support
                                                team to process your account
                                                deletion request.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>
                                                Cancel
                                            </AlertDialogCancel>
                                            <AlertDialogAction
                                                onClick={handleDeleteAccount}
                                                className="bg-destructive text-destructive-foreground"
                                            >
                                                support@getoptifi.com
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="optimizations" className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Optimization Settings</CardTitle>
                                <CardDescription>
                                    Configure default optimization settings for
                                    your products. These settings will be used
                                    when optimizing your products unless
                                    overridden on a per-product basis.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <Form {...optimizationForm}>
                                    <form
                                        onSubmit={optimizationForm.handleSubmit(
                                            handleOptimizationSubmit
                                        )}
                                        className="space-y-6"
                                    >
                                        <FormField
                                            control={optimizationForm.control}
                                            name="minPrice"
                                            render={({ field }) => (
                                                <FormField
                                                    control={
                                                        optimizationForm.control
                                                    }
                                                    name="maxPrice"
                                                    render={({
                                                        field: maxField,
                                                    }) => (
                                                        <FormItem>
                                                            <FormLabel>
                                                                Price Range (
                                                                {field.value.toFixed(
                                                                    0
                                                                )}
                                                                % to{" "}
                                                                {maxField.value.toFixed(
                                                                    0
                                                                )}
                                                                %)
                                                            </FormLabel>
                                                            <Slider
                                                                min={-100}
                                                                max={100}
                                                                step={1}
                                                                value={[
                                                                    field.value,
                                                                    maxField.value,
                                                                ]}
                                                                onValueChange={(
                                                                    values
                                                                ) => {
                                                                    field.onChange(
                                                                        values[0]
                                                                    );
                                                                    maxField.onChange(
                                                                        values[1]
                                                                    );
                                                                }}
                                                            />
                                                            <FormDescription>
                                                                Set the default
                                                                minimum and
                                                                maximum price
                                                                range to be used
                                                                when optimizing
                                                                your products,
                                                                as a relative
                                                                percentage of
                                                                the current
                                                                price.
                                                            </FormDescription>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />
                                            )}
                                        />

                                        <FormField
                                            control={optimizationForm.control}
                                            name="cost"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>
                                                        Default Cost (
                                                        {field.value.toFixed(0)}
                                                        %)
                                                    </FormLabel>
                                                    <div className="flex gap-4">
                                                        <FormControl className="flex-1">
                                                            <Slider
                                                                min={0}
                                                                max={100}
                                                                step={1}
                                                                value={[
                                                                    field.value,
                                                                ]}
                                                                onValueChange={(
                                                                    value
                                                                ) =>
                                                                    field.onChange(
                                                                        value[0]
                                                                    )
                                                                }
                                                            />
                                                        </FormControl>
                                                        <FormControl>
                                                            <input
                                                                type="number"
                                                                className="w-24 h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors"
                                                                min={0}
                                                                max={1000}
                                                                step={0.1}
                                                                value={
                                                                    field.value
                                                                }
                                                                onChange={(e) =>
                                                                    field.onChange(
                                                                        Number(
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    )
                                                                }
                                                            />
                                                        </FormControl>
                                                    </div>
                                                    <FormDescription>
                                                        Set the default cost
                                                        price for new products
                                                        as a relative percentage
                                                        of the current price.
                                                    </FormDescription>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <FormField
                                            control={optimizationForm.control}
                                            name="optimizationInterval"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>
                                                        Maximum Update Frequency
                                                    </FormLabel>
                                                    <Select
                                                        onValueChange={(
                                                            value
                                                        ) =>
                                                            field.onChange(
                                                                Number(value)
                                                            )
                                                        }
                                                        value={field.value.toString()}
                                                    >
                                                        <FormControl>
                                                            <SelectTrigger>
                                                                <SelectValue placeholder="Select frequency" />
                                                            </SelectTrigger>
                                                        </FormControl>
                                                        <SelectContent>
                                                            <SelectItem value="86400">
                                                                Daily
                                                            </SelectItem>
                                                            <SelectItem value="172800">
                                                                Every 2 days
                                                            </SelectItem>
                                                            <SelectItem value="259200">
                                                                Every 3 days
                                                            </SelectItem>
                                                            <SelectItem value="604800">
                                                                Weekly
                                                            </SelectItem>
                                                            <SelectItem value="1209600">
                                                                Every 2 weeks
                                                            </SelectItem>
                                                            <SelectItem value="2592000">
                                                                Monthly
                                                            </SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                    <FormDescription>
                                                        What is the maximum
                                                        frequency that prices
                                                        can be updated at? Our
                                                        algorithm will never
                                                        update prices more often
                                                        than this.
                                                    </FormDescription>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <Button
                                            type="submit"
                                            className="w-[120px]"
                                            disabled={loading}
                                        >
                                            <LoadingButtonContent
                                                loading={loading}
                                            >
                                                Save Changes
                                            </LoadingButtonContent>
                                        </Button>
                                    </form>
                                </Form>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="timezones" className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Market Timezones</CardTitle>
                                <CardDescription>
                                    Set the core timezone for each market. This
                                    will be used when updating prices for that
                                    market. Prices are usually updated at 2am in
                                    the timezone of the market.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-6">
                                    {shop?.markets?.map((market: any) => (
                                        <div
                                            key={market.id}
                                            className="flex flex-col gap-2"
                                        >
                                            <div className="text-sm font-medium">
                                                {market.name}
                                            </div>
                                            <TimezoneSelector
                                                currentTimezone={getMarketTimezone(
                                                    market.handle
                                                )}
                                                onSelect={(tz: string) => {
                                                    handleMarketTimezoneChange(
                                                        market.handle,
                                                        tz
                                                    );
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </div>
            </Tabs>
        </div>
    );
}

export default Settings;
